import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useApp } from '../components/AppProvider';
import { EXCLUDE_REFERENTI } from '../lib/utils/contactPollution';

export interface EmailContact {
  id: string;
  email: string;
  name: string;
  type: 'client' | 'contact';
  clientName?: string; // For contacts, includes the client name
}

export function useClientsAndContacts() {
  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { session } = useApp();

  const loadContacts = async (searchTerm: string = '') => {
    if (!session?.user?.id) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const organizationId = session.user.user_metadata?.organization_id;
      if (!organizationId) {
        throw new Error('Organization not found');
      }

      // Build search condition for clients.
      // Modello Contatti/Aziende first-class: EXCLUDE_REFERENTI toglie le persone-referente
      // dal bucket "client" (altrimenti comparirebbero mislabellate col nome-persona). I
      // referenti tornano nel bucket "contact" via client_relationships (sotto).
      let clientQuery = supabase
        .from('clients')
        .select('id, business_name, email, contact_name')
        .eq('organization_id', organizationId)
        .or(EXCLUDE_REFERENTI)
        .not('email', 'is', null)
        .neq('email', '');

      // Build search condition for LEGACY contacts (client_contacts — vuota per i clienti nuovi).
      let contactQuery = supabase
        .from('client_contacts')
        .select(`
          id,
          name,
          email,
          clients!inner(
            id,
            business_name
          )
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .not('email', 'is', null)
        .neq('email', '');

      // Referenti nuovi (persone via client_relationships('contatto')) org-wide.
      let relQuery = supabase
        .from('client_relationships')
        .select(`
          id,
          person:source_client_id ( business_name, email ),
          company:target_client_id ( business_name )
        `)
        .eq('organization_id', organizationId)
        .eq('relationship_type', 'contatto')
        .eq('is_active', true);

      // Apply search filters if searchTerm is provided
      if (searchTerm.trim()) {
        const searchPattern = `%${searchTerm.trim()}%`;

        clientQuery = clientQuery.or(
          `business_name.ilike.${searchPattern},trade_name.ilike.${searchPattern},email.ilike.${searchPattern},contact_name.ilike.${searchPattern}`
        );

        contactQuery = contactQuery.or(
          `name.ilike.${searchPattern},email.ilike.${searchPattern}`
        );
      }

      // Execute queries in parallel
      const [
        { data: clientsData, error: clientsError },
        { data: contactsData, error: contactsError },
        { data: relData, error: relError },
      ] = await Promise.all([
        clientQuery.limit(20),
        contactQuery.limit(20),
        relQuery.limit(50),
      ]);

      if (clientsError) throw clientsError;
      if (contactsError) throw contactsError;
      if (relError) throw relError;

      const emailContacts: EmailContact[] = [];
      const seenEmails = new Set<string>();

      // Add clients
      if (clientsData) {
        clientsData.forEach(client => {
          if (client.email) {
            emailContacts.push({
              id: `client-${client.id}`,
              email: client.email,
              name: client.business_name || client.contact_name || 'Cliente senza nome',
              type: 'client'
            });
            seenEmails.add(client.email.trim().toLowerCase());
          }
        });
      }

      // Add legacy contacts
      if (contactsData) {
        contactsData.forEach(contact => {
          if (contact.email) {
            emailContacts.push({
              id: `contact-${contact.id}`,
              email: contact.email,
              name: contact.name,
              type: 'contact',
              clientName: (contact.clients as any)?.business_name
            });
            seenEmails.add(contact.email.trim().toLowerCase());
          }
        });
      }

      // Add referenti nuovi (client_relationships → persona), filtro ricerca + dedup per email.
      if (relData) {
        const term = searchTerm.trim().toLowerCase();
        (relData as any[]).forEach(rel => {
          const p = Array.isArray(rel.person) ? rel.person[0] : rel.person;
          const co = Array.isArray(rel.company) ? rel.company[0] : rel.company;
          const email = p?.email as string | undefined;
          if (!email) return;
          const key = email.trim().toLowerCase();
          if (seenEmails.has(key)) return;
          const name = (p?.business_name as string) || email;
          if (term && !name.toLowerCase().includes(term) && !key.includes(term)) return;
          seenEmails.add(key);
          emailContacts.push({
            id: `rel-${rel.id}`,
            email,
            name,
            type: 'contact',
            clientName: (co?.business_name as string) || undefined,
          });
        });
      }

      // Sort by name
      emailContacts.sort((a, b) => a.name.localeCompare(b.name));

      setContacts(emailContacts);
    } catch (err) {
      console.error('Error loading clients and contacts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Load all contacts on mount
  useEffect(() => {
    loadContacts();
  }, [session?.user?.id]);

  return {
    contacts,
    loading,
    error,
    searchContacts: loadContacts,
    refreshContacts: () => loadContacts()
  };
}